import React, { useState } from "react";
import PropTypes from "prop-types";
import { FaChevronDown, FaChevronRight } from "react-icons/fa6";
import { formatTimestamp, formatShortTimestamp, responderStatusProps } from "./pdActivityUtils";
import PagerDutyResponderList from "./PagerDutyResponderList";
import styles from "./PagerDutyActivity.module.css";

const ESCALATION_PAGE_SIZE = 10;

/**
 * Single escalation group row — CONTROLLED: open/close state is owned by the
 * parent list (PagerDutyEscalationGroupList) so it can support Expand all /
 * Collapse all without prop-drilling internal state.
 */
function EscalationGroup({ group, index, open, onToggle }) {
  // Index guarantees a unique DOM id even when the same policy/level recurs
  // (e.g. a "created" then "exhausted" entry for the same policy+level).
  const groupId = `pd-escalation-${index}`;
  const respondersId = `${groupId}-responders`;

  const isExhausted =
    group.action === "exhausted" ||
    !group.responders ||
    group.responders.length === 0;

  return (
    <div className={styles.escalationGroup}>
      <button
        className={styles.escalationGroupHeader}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={respondersId}
        id={groupId}
      >
        <span className={styles.escalationPolicyName}>
          {group.policyName || "Unknown policy"}
          {group.level != null && (
            <span className={styles.escalationLevel}>Level {group.level}</span>
          )}
        </span>
        {isExhausted && (
          <span className={styles.escalationExhaustedBadge} aria-label="Escalation timed out">
            Timed out
          </span>
        )}
        <span className={styles.escalationTime}>{formatTimestamp(group.at)}</span>
        {/* Expand/collapse chevron sits at the right edge of the group row. */}
        <span className={styles.escalationToggleIcon} aria-hidden="true">
          {open ? <FaChevronDown /> : <FaChevronRight />}
        </span>
      </button>

      {open && (
        <div
          id={respondersId}
          role="list"
          aria-label={`Responders for ${group.policyName || "escalation"}${group.level != null ? ` level ${group.level}` : ""}`}
          className={styles.escalationResponderList}
        >
          {isExhausted ? (
            <p className={styles.escalationExhaustedMsg}>
              Escalation timed out — no response at this level.
            </p>
          ) : (
            group.responders.map((r, idx) => {
              const { cls, label } = responderStatusProps(r.status);
              return (
                <div
                  key={r.id || r.name || idx}
                  className={styles.escalationResponderRow}
                  role="listitem"
                >
                  <span className={styles.responderName}>{r.name || "Unknown"}</span>
                  <div className={styles.responderRight}>
                    {r.at && (
                      <span className={styles.responderTime}>
                        {formatShortTimestamp(r.at)}
                      </span>
                    )}
                    <span
                      className={`${styles.statusBadge} ${cls}`}
                      aria-label={`Status: ${label}`}
                    >
                      {label}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

EscalationGroup.propTypes = {
  index: PropTypes.number.isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  group: PropTypes.shape({
    policyId: PropTypes.string,
    policyName: PropTypes.string,
    level: PropTypes.number,
    action: PropTypes.oneOf(["created", "exhausted"]),
    at: PropTypes.string,
    responders: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        status: PropTypes.string,
        at: PropTypes.string,
      })
    ),
  }).isRequired,
};

/**
 * PagerDutyEscalationGroupList — owns the entire "Responders" section,
 * mirroring PagerDuty's Responders panel layout:
 *
 *   Responders                              ← main section heading
 *     Escalations                [unfold]   ← sub-heading + expand/collapse-all
 *       ▸ <policy name>  Level N  <time>     ← escalation groups (collapsed)
 *         <nested responders when expanded>
 *       ▸ ... more groups ...
 *     Individuals                           ← sub-heading
 *       <individual responder rows>
 *
 * The expand/collapse-all icon button sits on the "Escalations" sub-heading
 * row and controls the escalation groups only. Individual groups remain
 * independently togglable. Groups default to COLLAPSED.
 *
 * Props:
 *   escalations {Array}  escalation group objects
 *   individuals {Array}  directly-paged individual responder objects
 */
function PagerDutyEscalationGroupList({
  escalations,
  individuals,
  respondedCount = 0,
  responderCount = 0,
}) {
  const [showAll, setShowAll] = useState(false);

  // Visible slice is always a prefix so indices remain stable.
  const visible = showAll ? escalations : escalations.slice(0, ESCALATION_PAGE_SIZE);
  const hiddenCount = escalations.length - ESCALATION_PAGE_SIZE;

  // Open state: a Set of open group indices. Empty by default so every group
  // renders collapsed; the user expands the ones they care about (or Expand all).
  const [openSet, setOpenSet] = useState(() => new Set());

  // Section-level collapse — Responders (whole section) plus the Escalations and
  // Individuals sub-sections are each independently collapsible, all open by
  // default, matching the Timeline section's collapse behaviour.
  const [respondersOpen, setRespondersOpen] = useState(true);
  const [escalationsOpen, setEscalationsOpen] = useState(true);
  const [individualsOpen, setIndividualsOpen] = useState(true);

  const allOpen = escalations.length > 0 && openSet.size === escalations.length;

  const toggleOne = (idx) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allOpen) {
      setOpenSet(new Set());
    } else {
      setOpenSet(new Set(escalations.map((_, i) => i)));
    }
  };

  const hasEscalations = escalations.length > 0;
  const hasIndividuals = individuals.length > 0;

  // Both empty — single graceful empty state
  if (!hasEscalations && !hasIndividuals) {
    return (
      <section className={styles.sectionBlock} aria-labelledby="pd-responders-heading">
        <div className={styles.sectionHeaderRow}>
          <h4 id="pd-responders-heading" className={styles.sectionHeading}>
            Responders
          </h4>
        </div>
        <p className={styles.emptySection}>
          No responders recorded for this incident.
        </p>
      </section>
    );
  }

  return (
    <section className={styles.sectionBlock} aria-labelledby="pd-responders-heading">
      {/* Responders — collapsible main section (heading toggles the whole thing) */}
      <h4 className={styles.sectionHeadingWrapper}>
        <button
          className={styles.sectionToggleBtn}
          onClick={() => setRespondersOpen((prev) => !prev)}
          aria-expanded={respondersOpen}
          aria-controls="pd-responders-content"
        >
          <span id="pd-responders-heading" className={styles.sectionHeading}>
            Responders
          </span>
          {responderCount > 0 && (
            <span className={styles.respondedSummary}>
              ({respondedCount}/{responderCount} responded)
            </span>
          )}
          <span className={styles.sectionChevron} aria-hidden="true">
            {respondersOpen ? <FaChevronDown /> : <FaChevronRight />}
          </span>
        </button>
      </h4>

      {respondersOpen && (
        <div id="pd-responders-content" className={styles.sectionBlock}>
          {/* Escalations — collapsible sub-section */}
          <div className={styles.sectionBlock}>
            <h5 className={styles.sectionHeadingWrapper}>
              <button
                className={styles.sectionToggleBtn}
                onClick={() => setEscalationsOpen((prev) => !prev)}
                aria-expanded={escalationsOpen}
                aria-controls="pd-escalations-content"
              >
                <span id="pd-escalations-heading" className={styles.subHeading}>
                  Escalations
                </span>
                <span className={styles.sectionChevron} aria-hidden="true">
                  {escalationsOpen ? <FaChevronDown /> : <FaChevronRight />}
                </span>
              </button>
            </h5>

            {escalationsOpen && (
              <div id="pd-escalations-content">
                {hasEscalations && (
                  <div className={styles.escalationActionsRow}>
                    <button
                      className={styles.toggleAllBtn}
                      onClick={toggleAll}
                      aria-label={allOpen ? "Collapse all escalation groups" : "Expand all escalation groups"}
                    >
                      {allOpen ? "Collapse all" : "Expand all"}
                    </button>
                  </div>
                )}
                {hasEscalations ? (
                  <div
                    className={styles.escalationGroupList}
                    role="list"
                    aria-label="Escalation groups"
                  >
                    {visible.map((group, idx) => (
                      <div
                        key={`${group.policyId || group.policyName}-${group.level}-${idx}`}
                        role="listitem"
                      >
                        <EscalationGroup
                          group={group}
                          index={idx}
                          open={openSet.has(idx)}
                          onToggle={() => toggleOne(idx)}
                        />
                      </div>
                    ))}

                    {!showAll && hiddenCount > 0 && (
                      <button
                        className={styles.showMoreBtn}
                        onClick={() => setShowAll(true)}
                        aria-label={`Show ${hiddenCount} more escalation group${hiddenCount !== 1 ? "s" : ""}`}
                      >
                        Show {hiddenCount} more escalation{hiddenCount !== 1 ? "s" : ""}
                      </button>
                    )}
                    {showAll && escalations.length > ESCALATION_PAGE_SIZE && (
                      <button
                        className={styles.showMoreBtn}
                        onClick={() => setShowAll(false)}
                        aria-label="Show fewer escalation groups"
                      >
                        Show fewer escalations
                      </button>
                    )}
                  </div>
                ) : (
                  <p className={styles.emptySection}>No escalation policies triggered.</p>
                )}
              </div>
            )}
          </div>

          {/* Individuals — collapsible sub-section */}
          <div className={styles.sectionBlock}>
            <h5 className={styles.sectionHeadingWrapper}>
              <button
                className={styles.sectionToggleBtn}
                onClick={() => setIndividualsOpen((prev) => !prev)}
                aria-expanded={individualsOpen}
                aria-controls="pd-individuals-content"
              >
                <span id="pd-individuals-heading" className={styles.subHeading}>
                  Individuals
                </span>
                <span className={styles.sectionChevron} aria-hidden="true">
                  {individualsOpen ? <FaChevronDown /> : <FaChevronRight />}
                </span>
              </button>
            </h5>
            {individualsOpen && (
              <div id="pd-individuals-content">
                <PagerDutyResponderList
                  responders={individuals}
                  listLabel="Individuals"
                  emptyMessage="No individually-paged responders."
                />
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

PagerDutyEscalationGroupList.propTypes = {
  escalations: PropTypes.arrayOf(
    PropTypes.shape({
      policyId: PropTypes.string,
      policyName: PropTypes.string,
      level: PropTypes.number,
      action: PropTypes.oneOf(["created", "exhausted"]),
      at: PropTypes.string,
      responders: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          status: PropTypes.string,
          at: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  individuals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string,
      at: PropTypes.string,
    })
  ).isRequired,
  // Incident-level responder totals for the "(x/y responded)" header summary.
  respondedCount: PropTypes.number,
  responderCount: PropTypes.number,
};

export default PagerDutyEscalationGroupList;
